"use client";

import { ArrowUpRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useTheme } from "@/components/public/theme-provider";
import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { MegaMenu } from "@/components/public/MegaMenu";
import { megaMenuData, isPublicPage } from "@/lib/navigation";
import { ModernButton } from "@/components/ui/ModernButton";
import { getAuthUrl } from "@/lib/appStage";

// Mobile Menu Component with Slide Navigation
function MobileMenu({ megaMenuData, onItemClick, currentView, setCurrentView }: { 
  megaMenuData: typeof import('@/lib/navigation').megaMenuData, 
  onItemClick: () => void, 
  currentView: 'main' | string,
  setCurrentView: (view: 'main' | string) => void
}) {
  const handleMenuClick = (menuTitle: string) => {
    setCurrentView(menuTitle);
  };

  return (
    <div className="relative w-full h-full overflow-hidden">

      {/* Main Menu View */}
      <div className={`absolute inset-0 w-full h-full transition-all duration-300 ease-out ${currentView === 'main' ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`}>
        <div className="h-full overflow-y-auto scrollbar-hide px-4 sm:px-6 py-4 pb-8">
          {megaMenuData.map((menu, index) => (
            <div 
              key={index}
              className="transform transition-all duration-300"
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <button
                onClick={() => handleMenuClick(menu.title)}
                className="w-full py-4 flex items-center justify-between text-left rounded-xl hover:bg-gray-50/80 active:bg-gray-100 transition-all duration-200 group px-3 -mx-3"
              >
                <span className="font-semibold text-gray-900 text-base group-hover:text-gray-700 transition-colors duration-200">
                  {menu.title}
                </span>
                <svg className="w-5 h-5 text-gray-400 transition-all duration-200 group-hover:text-gray-600 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {index < megaMenuData.length - 1 && (
                <div className="border-t border-gray-100 my-1"></div>
              )}
            </div>
          ))}
          
          {/* Divider */}
          <div className="border-t border-gray-100 my-2"></div>
          
          <Link
            href="/contact"
            onClick={onItemClick}
            className="w-full py-4 flex items-center justify-between text-left rounded-xl hover:bg-gray-50/80 active:bg-gray-100 transition-all duration-200 group px-3 -mx-3"
          >
            <span className="font-semibold text-gray-900 text-base group-hover:text-gray-700 transition-colors duration-200">
              Contact
            </span>
          </Link>

          {/* HIDDEN ROUTE - Uncomment to show in navigation */}
          {/* <Link
            href="/pricing"
            onClick={onItemClick}
            className="w-full py-4 flex items-center justify-between text-left rounded-xl hover:bg-gray-50/80 active:bg-gray-100 transition-all duration-200 group px-3 -mx-3"
          >
            <span className="font-semibold text-gray-900 text-base group-hover:text-gray-700 transition-colors duration-200">
              Pricing
            </span>
          </Link> */}

          {/* Divider */}
          <div className="border-t border-gray-100 my-2"></div>

          {/* Auth Buttons */}
          <div className="space-y-3">
            <Link
              href={getAuthUrl('register')}
              onClick={onItemClick}
              className="block w-full py-3.5 text-center text-gray-700 font-semibold text-base border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-all duration-200"
            >
              Register
            </Link>
            <Link
              href={getAuthUrl('login')}
              onClick={onItemClick}
              className="w-full py-3.5 text-center bg-gray-900 text-white font-semibold text-base rounded-xl hover:bg-gray-800 active:bg-gray-950 transition-all duration-200 flex items-center justify-center gap-2 shadow-sm"
            >
              Sign in
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Sub-menu Views - Always rendered for smooth transitions */}
      {megaMenuData.map((menu: (typeof megaMenuData)[number], menuIndex: number) => (
        <div 
          key={menuIndex}
          className={`absolute inset-0 w-full h-full transition-all duration-300 ease-out ${currentView === menu.title ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
        >
          <div className="h-full overflow-y-auto scrollbar-hide px-4 sm:px-6 py-4 pb-8">
            {/* Sub-menu Content */}
            <div className="space-y-6">
              {menu.sections.map((section: (typeof menu.sections)[number], sectionIndex: number) => (
                <div 
                  key={sectionIndex}
                  className="transform transition-all duration-300"
                  style={{ transitionDelay: currentView === menu.title ? `${sectionIndex * 75}ms` : '0ms' }}
                >
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">
                    {section.title}
                  </h3>
                  <div className="space-y-1">
                    {section.items.map((item: (typeof section.items)[number], itemIndex: number) => (
                      <Link
                        key={itemIndex}
                        href={item.href}
                        onClick={onItemClick}
                        className="block py-3 px-3 -mx-3 rounded-xl hover:bg-gray-50/80 active:bg-gray-100 transition-all duration-200 group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900 text-base group-hover:text-gray-700 transition-colors duration-200">
                            {item.title}
                          </span>
                          <ArrowUpRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 flex-shrink-0" />
                        </div>
                        {item.description && (
                          <p className="text-sm text-gray-500 leading-relaxed group-hover:text-gray-600 transition-colors duration-200 mt-1">
                            {item.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                  {sectionIndex < menu.sections.length - 1 && (
                    <div className="border-t border-gray-100 my-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function LandingTopBar() {
  console.log('[LandingTopBar] Rendered');
  useTheme(); // Keep for theme context
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuView, setMobileMenuView] = useState<'main' | string>('main');
  const router = useRouter();
  const pathname = usePathname();

  // Disable body scroll when mobile menu is open and reset view on close
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      // Reset to main view when menu closes for fresh experience on reopen
      const timeout = setTimeout(() => setMobileMenuView('main'), 300);
      return () => clearTimeout(timeout);
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    console.log('[LandingTopBar] useEffect - component mounted');
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    console.log('[LandingTopBar] Logo clicked, pathname:', pathname);
    if (pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      router.push('/');
    }
  };

  // Check if we're on a public page
  const publicPage = isPublicPage(pathname || '');

  // Super modern professional top bar with dark theme
  return (
    <header 
      className={`absolute top-0 left-0 right-0 z-50 ${mobileMenuOpen ? 'bg-white' : ''} sm:px-0 md:px-6 xl:px-6`}
    >
      <div className="w-full">
        {/* Consistent responsive padding: px-4 mobile, px-6 tablet, grid-aligned desktop */}
        <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%]">
          <div className="flex items-center h-16">{/* Back to h-16 */}
          {/* Logo with subtle animation */}
          <div 
            onClick={handleLogoClick} 
            className="cursor-pointer group transition-all duration-300 flex-shrink-0"
          >
            <div className="relative">
              {/* Desktop Logo - consistent size */}
              <Image
                src={publicPage ? "/assets/REPLENICO/logos/replenico-logo-horizontal-stacked-black.png" : "/assets/REPLENICO/logos/replenico-logo-horizontal-stacked-white.png"}
                alt="Replenico Logo"
                width={180}
                height={54}
                style={{ width: 'auto', height: '3rem' }}
                className="hidden lg:block object-contain hover:opacity-80 transition-opacity duration-300"
                priority
              />
              
              {/* Tablet/Mobile Logo - consistent size */}
              {!mobileMenuOpen && (
                <Image
                  src={publicPage ? "/assets/REPLENICO/logos/replenico-logo-text-dark.png" : "/assets/REPLENICO/logos/replenico-logo-text-white.png"}
                  alt="Replenico Logo"
                  width={120}
                  height={36}
                  style={{ width: 'auto', height: '2rem' }}
                  className="lg:hidden object-contain"
                  priority
                />
              )}
              
              {/* Mobile Menu Header Elements - Only show when menu is open */}
              {mobileMenuOpen && (
                <div className="lg:hidden">
                  {mobileMenuView === 'main' ? (
                    <Image
                      src="/assets/REPLENICO/logos/replenico-logo-text-dark.png"
                      alt="Replenico Logo"
                      width={120}
                      height={36}
                      style={{ width: 'auto', height: '2rem' }}
                      className="object-contain"
                    />
                  ) : (
                    <button
                      onClick={() => setMobileMenuView('main')}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-all duration-300 group cursor-pointer"
                    >
                      <svg className="w-6 h-6 text-gray-700 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              <div className="absolute inset-0 bg-[color:var(--homepage-accent)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm -z-10"></div>
            </div>
          </div>

          {/* Navigation with modern styling */}
          <nav className="hidden lg:flex items-center space-x-1 ml-8">
            {megaMenuData.map((menu: any, index: number) => (
              <MegaMenu
                key={index}
                title={menu.title}
                sections={menu.sections}
                isPublicPage={publicPage}
              />
            ))}
            <Link
              href="/contact"
              className={`relative px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 group ${
                publicPage 
                  ? 'text-[color:var(--homepage-primary)] hover:text-[var(--homepage-accent-purple)]' 
                  : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10">Contact</span>
            </Link>
            {/* HIDDEN ROUTE - Uncomment to show in navigation */}
            {/* <Link
              href="/pricing"
              className={`relative px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 group ${
                publicPage 
                  ? 'text-[color:var(--homepage-primary)] hover:text-[var(--homepage-accent-purple)]' 
                  : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10">Pricing</span>
            </Link> */}
          </nav>


          {/* Auth Buttons with modern styling - Hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-3 ml-auto">
            <Link
              href={getAuthUrl('register')}
              className={`relative px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 group ${
                publicPage 
                  ? 'text-[color:var(--homepage-primary)] hover:text-[#8c52ff]' 
                  : 'text-[color:var(--homepage-navbar-text)] hover:text-[color:var(--homepage-navbar-hover)]'
              }`}
            >
              <span className="relative z-10">Register</span>
            </Link>
            <ModernButton
              href={getAuthUrl('login')}
              variant="primary"
              size="md"
              showArrow={true}
            >
              Login
            </ModernButton>
          </div>

          {/* Mobile Menu Button - Top Right */}
          {!mobileMenuOpen && (
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-1 rounded-lg transition-all duration-300 hover:bg-white/10 ml-auto"
            >
              <Image
                src={publicPage ? "/assets/REPLENICO/logos/replenico-logo-3-lines-dark.png" : "/assets/REPLENICO/logos/replenico-logo-3-lines-white.png"}
                alt="Menu"
                width={48}
                height={48}
                className="h-10 w-auto object-fill transition-all duration-300"
              />
            </button>
          )}

        </div>
      </div>
    </div>
      {/* Mobile Menu Dropdown */}
      <div 
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-300 ease-out ${
          mobileMenuOpen 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-white transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        />
        
        {/* Content */}
        <div className="relative h-full flex flex-col">
          {/* Fixed Header Area */}
          <div className="h-16 border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 flex-shrink-0 bg-white">
            {/* Left side - Logo or Back button */}
            <div className="flex items-center min-w-[48px]">
              {mobileMenuView === 'main' ? (
                <Image
                  src="/assets/REPLENICO/logos/replenico-logo-text-dark.png"
                  alt="Replenico Logo"
                  width={120}
                  height={36}
                  style={{ width: 'auto', height: '2rem' }}
                  className="object-contain"
                />
              ) : (
                <button
                  onClick={() => setMobileMenuView('main')}
                  className="p-2 -ml-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 group cursor-pointer"
                >
                  <svg className="w-6 h-6 text-gray-600 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Center - Title when in nested view */}
            <div className="flex-1 flex justify-center">
              {mobileMenuView !== 'main' && (
                <h2 className="text-gray-900 font-bold text-lg tracking-tight">
                  {megaMenuData.find((menu: any) => menu.title === mobileMenuView)?.title}
                </h2>
              )}
            </div>

            {/* Right side - Close button */}
            <div className="min-w-[48px] flex justify-end">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 -mr-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 group"
              >
                <svg className="w-6 h-6 text-gray-600 group-hover:text-gray-800 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-hidden">
            <MobileMenu 
              megaMenuData={megaMenuData} 
              onItemClick={() => setMobileMenuOpen(false)}
              currentView={mobileMenuView}
              setCurrentView={setMobileMenuView}
            />
          </div>
        </div>
      </div>
    </header>
  );
}